/**
 * AssistantMessage.parts.tsx
 *
 * Colocated helpers extracted from ChatMessage.tsx. Contains small presentational
 * sub-pieces used exclusively by AssistantMessage.tsx:
 *   - PiaAvatarIcon, PiaAvatar
 *   - StarIcon, BoltIcon
 *   - RoutingBadge
 *   - DocumentRetrievalCard
 *   - ChartLoadingPlaceholder
 *   - reactNodeToString
 *   - applyHighlightsToChildren
 *   - formatTimestamp, formatTimeOnly
 *
 * None of these components contain application business logic.
 * DocumentRetrievalCard owns two useEffects for its auto-collapse timer.
 */

import React, { useState, useEffect, useRef } from "react";
import { highlightText } from "../../utils/highlightText.js";

// ---------------------------------------------------------------------------
// Timestamp formatters
// ---------------------------------------------------------------------------

/** Formats a Date as "9 Apr, 14:32" using British locale and London timezone. */
export function formatTimestamp(date: Date): string {
	return date.toLocaleString("en-GB", {
		day: "numeric",
		month: "short",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
		timeZone: "Europe/London",
	});
}

/**
 * Formats the HH:mm portion of a timestamp in Europe/London timezone.
 * Used for bubble aria-labels (e.g. "PIA at 14:32").
 */
export function formatTimeOnly(date: Date): string {
	return date.toLocaleString("en-GB", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
		timeZone: "Europe/London",
	});
}

// ---------------------------------------------------------------------------
// Small icon components
// ---------------------------------------------------------------------------

/**
 * PIA brand icon — faithful inline transcription of the Claude Design handoff
 * favicon (assets/favicon.svg): slate rounded-square tile with a stylised
 * amber "P" letterform. Rendered as a self-contained 20×20 SVG so no external
 * asset path is required.
 *
 * Geometry derived from the 32×32 handoff SVG, scaled to 20×20 (factor 0.625):
 *   - Chat bubble outline (white stroke on slate fill) → decorative backdrop
 *   - "P" letterform filled in amber (#f5a623)
 */
export function PiaAvatarIcon() {
	return (
		<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
			{/* Slate rounded-square tile background */}
			<rect width="20" height="20" rx="4.375" fill="#1e293b" />
			{/* Chat-bubble backdrop (white stroke, transparent fill) */}
			<path
				d="M3.75 6.25 a2.19 2.19 0 0 1 2.19 -2.19 h8.12 a2.19 2.19 0 0 1 2.19 2.19 v5 a2.19 2.19 0 0 1 -2.19 2.19 h-5.94 l-2.81 2.5 v-2.5 a2.19 2.19 0 0 1 -1.56 -2.19 Z"
				fill="#1e293b"
				stroke="white"
				strokeWidth="1"
				strokeLinejoin="round"
			/>
			{/* Amber "P" letterform */}
			<path
				d="M7.5 5.625 h3.44 a2.19 2.19 0 0 1 0 4.375 H9.06 v2.5 H7.5 Z M9.06 6.875 v1.75 h1.88 a0.75 0.75 0 0 0 0 -1.75 Z"
				fill="#f5a623"
			/>
		</svg>
	);
}

/** Star icon — used on the Pro routing badge. */
export function StarIcon() {
	return (
		<svg viewBox="0 0 9 9" fill="currentColor" aria-hidden="true" style={{ width: 8, height: 8, flexShrink: 0 }}>
			<path d="M4.5 0L6 3.5H9L6.5 5.5L7.5 9L4.5 7L1.5 9L2.5 5.5L0 3.5H3L4.5 0Z" />
		</svg>
	);
}

/** Bolt/zap icon — used on the Flash routing badge. */
export function BoltIcon() {
	return (
		<svg viewBox="0 0 9 9" fill="currentColor" aria-hidden="true" style={{ width: 8, height: 8, flexShrink: 0 }}>
			<path d="M5.5 0L2 5h2.5L3.5 9L7 4H4.5L5.5 0z" />
		</svg>
	);
}

/** Small dark rounded square with the PIA chart icon. */
export function PiaAvatar() {
	return (
		<div className="message-pia-avatar" aria-hidden="true">
			<PiaAvatarIcon />
		</div>
	);
}

// ---------------------------------------------------------------------------
// Routing badge
// ---------------------------------------------------------------------------

/** Derives a short, readable model label from the full model string. */
function modelLabel(model: string): string {
	if (model.toLowerCase().includes("pro")) return "Pro";
	if (model.toLowerCase().includes("flash-lite")) return "Flash Lite";
	if (model.toLowerCase().includes("flash")) return "Flash";
	return model;
}

/**
 * Classification badge showing routing decision.
 * COMPLEX → Pro gets orange treatment; SIMPLE/MODERATE → Flash gets green.
 */
export function RoutingBadge({ complexity, model }: { complexity: string; model: string }) {
	const isComplex = complexity.toUpperCase() === "COMPLEX";
	const label = modelLabel(model);
	const displayComplexity = complexity.toUpperCase();

	return (
		<span
			className={`message-routing-badge ${isComplex ? "message-routing-badge--pro" : "message-routing-badge--flash"}`}
			aria-label={`Routed to ${label} (${displayComplexity})`}
		>
			{isComplex ? <StarIcon /> : <BoltIcon />}
			{displayComplexity} → {label}
		</span>
	);
}

// ---------------------------------------------------------------------------
// Document retrieval card
// ---------------------------------------------------------------------------

/**
 * Document retrieval status card.
 *
 * Shown between the message header and the content/thinking-dots while a
 * document search is in progress or has completed. The "found" state
 * auto-collapses after 2 seconds (source pills at the bottom replace it
 * once streaming is complete). "none" and "unavailable" remain visible.
 */
export function DocumentRetrievalCard({
	isSearchingDocuments,
	retrievalStatus,
	isStreaming,
}: {
	isSearchingDocuments?: boolean;
	retrievalStatus?: "found" | "none" | "unavailable";
	isStreaming?: boolean;
}) {
	const [collapsed, setCollapsed] = useState(false);
	const collapseTimerRef = useRef<ReturnType<typeof setTimeout>>();

	// Auto-collapse "found" card after 2 seconds once streaming completes
	useEffect(() => {
		if (retrievalStatus === "found" && !isSearchingDocuments && !isStreaming) {
			collapseTimerRef.current = setTimeout(() => {
				setCollapsed(true);
			}, 2000);
		}
		return () => {
			if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
		};
	}, [retrievalStatus, isSearchingDocuments, isStreaming]);

	// Reset collapsed state when a new search begins
	useEffect(() => {
		if (isSearchingDocuments) {
			setCollapsed(false);
		}
	}, [isSearchingDocuments]);

	// Determine whether to show the card at all
	const isActive = isSearchingDocuments === true;
	const hasStatus = retrievalStatus !== undefined;

	// Hide "found" card once collapsed (source pills take over after streaming)
	if (!isActive && !hasStatus) return null;
	if (retrievalStatus === "found" && collapsed) return null;

	// --- Searching state ---
	if (isActive) {
		return (
			<div className="retrieval-card retrieval-card--searching" role="status" aria-label="Searching documents">
				<div className="retrieval-card-icon retrieval-card-icon--searching" aria-hidden="true">
					<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="12" height="12">
						<circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.4" />
						<path d="M9.5 9.5L12.5 12.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
					</svg>
				</div>
				<span className="retrieval-card-text">Searching documents…</span>
			</div>
		);
	}

	// --- Found state ---
	if (retrievalStatus === "found") {
		return (
			<div className="retrieval-card retrieval-card--found" role="status" aria-label="Documents found">
				<div className="retrieval-card-icon" aria-hidden="true">
					<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" width="12" height="12">
						<circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.3" />
						<path d="M3.5 6L5.2 7.8L8.5 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</div>
				<span className="retrieval-card-text">Documents found</span>
			</div>
		);
	}

	// --- None state ---
	if (retrievalStatus === "none") {
		return (
			<div className="retrieval-card retrieval-card--none" role="status" aria-label="No relevant documents found">
				<div className="retrieval-card-icon" aria-hidden="true">
					<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" width="12" height="12">
						<circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.3" />
						<path d="M4 6h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
					</svg>
				</div>
				<span className="retrieval-card-text">No relevant documents found</span>
			</div>
		);
	}

	// --- Unavailable state ---
	if (retrievalStatus === "unavailable") {
		return (
			<div className="retrieval-card retrieval-card--unavailable" role="status" aria-label="Document search unavailable">
				<div className="retrieval-card-icon" aria-hidden="true">
					<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" width="12" height="12">
						<path d="M6 1L11.2 10H0.8L6 1Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
						<path d="M6 5v2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
						<circle cx="6" cy="9" r="0.6" fill="currentColor" />
					</svg>
				</div>
				<span className="retrieval-card-text">Document search unavailable</span>
			</div>
		);
	}

	return null;
}

// ---------------------------------------------------------------------------
// Chart loading placeholder
// ---------------------------------------------------------------------------

/** Shown when classification indicates VISUALISATION intent but chartData hasn't arrived yet. */
export function ChartLoadingPlaceholder() {
	return (
		<div className="message-chart-loading" role="status" aria-label="Generating chart">
			<div className="message-chart-loading-icon" aria-hidden="true">
				{/*
				 * Each rect is independently animated via CSS.
				 * transform-box: fill-box + transform-origin: bottom centre
				 * allows scaleY to grow from the baseline upward.
				 * The three bars pulse at staggered delays, simulating a
				 * chart being drawn.
				 */}
				<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
					<rect className="chart-loading-bar chart-loading-bar--1" x="2"  y="10" width="4" height="8"  rx="1" fill="currentColor" />
					<rect className="chart-loading-bar chart-loading-bar--2" x="8"  y="6"  width="4" height="12" rx="1" fill="currentColor" />
					<rect className="chart-loading-bar chart-loading-bar--3" x="14" y="2"  width="4" height="16" rx="1" fill="currentColor" />
				</svg>
			</div>
			<span>Generating chart…</span>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Utility — safely coerce a React node tree to a plain string.
// String(children) yields "[object Object]" when children is a ReactElement
// (which can happen during streaming). This walks the node tree instead.
// ---------------------------------------------------------------------------

export function reactNodeToString(node: React.ReactNode): string {
	if (typeof node === "string") return node;
	if (typeof node === "number") return String(node);
	if (Array.isArray(node)) return node.map(reactNodeToString).join("");
	return "";
}

/**
 * Walk React markdown children and apply `highlightText` to string leaves
 * while passing React elements (e.g. PersonPill, ContactPill, <strong>, etc.)
 * through unchanged. Returns a flat array of ReactNodes suitable for rendering.
 */
export function applyHighlightsToChildren(children: React.ReactNode): React.ReactNode[] {
	const result: React.ReactNode[] = [];
	React.Children.forEach(children, (child, idx) => {
		if (typeof child === "string") {
			const highlighted = highlightText(child);
			highlighted.forEach((node, hIdx) => {
				if (typeof node === "string") {
					result.push(node);
				} else {
					// Clone to stamp a unique key based on position
					result.push(
						<React.Fragment key={`hl-${idx}-${hIdx}`}>{node}</React.Fragment>,
					);
				}
			});
		} else if (typeof child === "number") {
			result.push(String(child));
		} else {
			result.push(child);
		}
	});
	return result;
}
